import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { COLORS } from "../../../Constants/colors";
import { motion } from "framer-motion";

// Card fade-in animation
const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.15,
      duration: 2,
      ease: "easeOut",
    },
  }),
};

const GridWrapper = styled.div`
  background-color: ${COLORS.background};
  padding: 3rem 1rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  display: flex;
  flex-direction: row;
  background-color: ${COLORS.cardBackground};
  color: ${COLORS.textPrimary};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid ${COLORS.border};
  min-height: 600px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-6px);
  }

  @media (max-width: 1100px) {
    flex-direction: column;
    min-height: auto;
    text-align: center;
  }
`;

const SlideImage = styled.img`
  width: 48%;
  object-fit: cover;
  height: auto;
  transition: transform 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.04);
  }

  @media (max-width: 1100px) {
    width: 100%;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Heading = styled.h3`
  font-size: 0.85rem;
  color: ${COLORS.primary};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1.2px;
`;

const Title = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${COLORS.textPrimary};
`;

const Body = styled.p`
  font-size: 1.2rem;
  color: ${COLORS.textSecondary};
  line-height: 1.6;
  flex-grow: 1;
`;

const ExpandButton = styled.button`
  display: inline-block;
  padding: 10px 16px;
  border: 2px solid ${COLORS.primary};
  color: ${COLORS.primary};
  background: transparent;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease-in-out;
  &:hover {
    background: ${COLORS.primary};
    color: ${COLORS.buttonText};
    transform: translateY(-2px);
  }
`;

const ClientCarousel = ({ slides }) => {
  const navigate = useNavigate();
  return (
    <GridWrapper>
      {slides.map((slide, i) => (
        <Card
          key={slide.id}
          custom={i}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={cardVariants}
        >
          <SlideImage src={slide.image} alt={slide.title} />
          <Content>
            <div>
              <Heading>{slide.heading}</Heading>
              <Title>{slide.title}</Title>
              <Body>{slide.body}</Body>
              <ExpandButton onClick={() => navigate(slide.link)}>
                Read more →
              </ExpandButton>
            </div>
          </Content>
        </Card>
      ))}
    </GridWrapper>
  );
};

export default ClientCarousel;

